import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { PencilIcon, PlusIcon, PrinterIcon } from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { HseDialogFooter } from "../../../../components/dashboard/hse/HseDialogFooter";
import { HseListCard, HsePageBody } from "../../../../components/dashboard/hse/HsePageBody";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import {
  createClimateRisk,
  exportClimateRiskPdf,
  fetchClimateRisks,
  updateClimateRisk,
} from "../../../../api/hse/risk";
import { HseRowActionsMenu } from "../../../../components/dashboard/hse/HseRowActionsMenu";
import { RiskLevelBadge } from "../../../../components/dashboard/hse/risk/RiskLevelBadge";
import { RiskSubnav } from "../../../../components/dashboard/hse/risk/RiskSubnav";
import { HseFieldInfoLabel } from "../../../../components/dashboard/hse/HseFieldInfoLabel";
import {
  CLIMATE_CATEGORIES,
  CLIMATE_HORIZONS,
  emptyClimateForm,
  formatDisplayDate,
  riskApiError,
} from "../../../../components/dashboard/hse/risk/riskUtils";

const inputClass =
  "mt-2 w-full rounded-[14px] border-[1.5px] border-slate-200 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#1463C7]";

export const HseKsClimateRisk = () => {
  const { t } = useTranslation();
  const canCreate = useUserHasAccess({ role: "hse_ks_risk", action: "create" });
  const canEdit = useUserHasAccess({ role: "hse_ks_risk", action: "edit" });
  const [editing, setEditing] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ query: "", climate_categories: [] });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyClimateForm());

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetchClimateRisks({
        query: filters.query,
        risk_category: filters.climate_categories?.[0],
        per_page: 50,
      });
      setItems(response.data || []);
    } catch (err) {
      setError(riskApiError(err) || t("hse.risk.climate.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.query, filters.climate_categories]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyClimateForm());
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm(emptyClimateForm(item));
    setOpen(true);
  };

  const handleExport = async (item) => {
    try {
      await exportClimateRiskPdf(item.id, `hse-klimarisiko-${item.id}.pdf`);
      toast.success(t("hse.risk.pdf_ready"));
    } catch (err) {
      toast.error(err.message || t("hse.risk.pdf_failed"));
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      toast.error(t("hse.risk.error_title"));
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await updateClimateRisk(editing.id, form);
        toast.success(t("hse.risk.climate.updated"));
      } else {
        await createClimateRisk(form);
        toast.success(t("hse.risk.climate.created"));
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyClimateForm());
      load();
    } catch (err) {
      toast.error(riskApiError(err) || t("hse.risk.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <HsePageChrome
      variant="list"
      title={t("hse.risk.climate.title")}
      subtitle={t("hse.risk.climate.subtitle")}
      listCount={loading ? null : items.length}
      mainButton={
        canCreate
          ? { label: t("hse.risk.climate.new"), icon: PlusIcon, action: openCreate }
          : undefined
      }
    >
      <HsePageBody>
        <RiskSubnav active="climate" />
        <div>
          <ModuleFilter
            filterCard
            hideStatusFilter
            searchPlaceholder={t("hse.risk.search")}
            initialFilters={filters}
            onFiltersChange={(next) =>
              setFilters({
                query: next.query || "",
                climate_categories: next.climate_categories || [],
              })
            }
            customDropdownFilters={[
              {
                key: "climate_categories",
                label: t("hse.risk.fields.category"),
                options: CLIMATE_CATEGORIES.map((category) => ({
                  value: category,
                  label: t(`hse.risk.climate.category.${category}`),
                  class: "bg-slate-100 text-slate-800",
                })),
              },
            ]}
          />
        </div>
        <HseListCard>
          {loading ? (
            <div className="p-8">
              <ResourceLoading />
            </div>
          ) : error ? (
            <ResourceError errors={[error]} onRetry={load} />
          ) : items.length === 0 ? (
            <div className="px-6 py-16">
              <ResourceEmpty message={t("hse.risk.climate.empty")} />
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="flex cursor-pointer items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50"
                  onClick={() => canEdit && openEdit(item)}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-900">{item.title}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {t(`hse.risk.climate.category.${item.risk_category}`)}
                      {" · "}
                      {t(`hse.risk.climate.horizon.${item.time_horizon}`)}
                      {item.updated_at ? ` · ${formatDisplayDate(item.updated_at)}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <RiskLevelBadge level={item.risk_level} />
                    <HseRowActionsMenu
                      label={t("hse.risk.actions")}
                      items={[
                        {
                          label: t("hse.risk.climate.edit"),
                          icon: PencilIcon,
                          hidden: !canEdit,
                          action: () => openEdit(item),
                        },
                        {
                          label: t("hse.risk.pdf"),
                          icon: PrinterIcon,
                          action: () => handleExport(item),
                        },
                      ]}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </HseListCard>
      </HsePageBody>

      <PMDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        title={editing ? t("hse.risk.climate.edit") : t("hse.risk.climate.new")}
        size="lg"
        closeOnOutsideClick
      >
        <form onSubmit={handleCreate} className="space-y-4">
          <label className="block">
            <HseFieldInfoLabel
              label={t("hse.risk.fields.title")}
              infoTitle="hse.risk.info.climate.title"
              infoBody="hse.risk.info.climate.body"
            />
            <input
              className={inputClass}
              value={form.title}
              onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.fields.category")}</span>
              <select
                className={inputClass}
                value={form.risk_category}
                onChange={(event) =>
                  setForm((current) => ({ ...current, risk_category: event.target.value }))
                }
              >
                {CLIMATE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {t(`hse.risk.climate.category.${category}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.climate.horizon_label")}</span>
              <select
                className={inputClass}
                value={form.time_horizon}
                onChange={(event) =>
                  setForm((current) => ({ ...current, time_horizon: event.target.value }))
                }
              >
                {CLIMATE_HORIZONS.map((horizon) => (
                  <option key={horizon} value={horizon}>
                    {t(`hse.risk.climate.horizon.${horizon}`)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-[13px] font-bold text-slate-700">{t("hse.risk.fields.description")}</span>
            <textarea
              rows={3}
              className={inputClass}
              value={form.description}
              onChange={(event) =>
                setForm((current) => ({ ...current, description: event.target.value }))
              }
            />
          </label>
          <HseDialogFooter
            onCancel={() => setOpen(false)}
            cancelLabel={t("hse.risk.cancel")}
            submitLabel={saving ? t("hse.risk.saving") : t("hse.risk.save")}
            submitting={saving}
          />
        </form>
      </PMDialog>
    </HsePageChrome>
  );
};
